import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Divider, Paper } from "@mui/material";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import EmptyState from "../Common/EmptyState/EmptyState";
import SearchForm from "./SearchForm";
import DashboardHeader from "./DashboardHeader";
import IndoorDashboard from "./IndoorDashboard";
import OutdoorDashboard from "./OutdoorDashboard";
import ComparisonSection from "./ComparisonSection";
import LocationSection from "./LocationSection";
import DashboardSkeleton from "./DashboardSkeleton";
import {
  fetchSensorHubDeviceStatus,
  fetchSmartRoomHomeDetails,
  fetchWeatherDetails,
} from "../../action/smartRoom";
import {
  ERROR_MESSAGES,
  INDOOR_POLL_INTERVAL_MS,
  OUTDOOR_POLL_INTERVAL_MS,
} from "../../constants/smartRoomConstants";
import { getSmartRoomApiErrorMessage } from "../../utils/smartRoomApiErrors";
import {
  extractHomeCoordinates,
  findOdmsensorHubInSpace,
  findSpaceById,
  formatLocalDateTime,
  listHomeSpaces,
  mapIndoorStatus,
  mapWeatherStatus,
  normalizeHomeDetails,
  resolveIndoorUpdatedLabel,
} from "../../utils/smartRoomHelpers";

const emptySpaceMeta = {
  spaceId: "",
  spaceName: "",
  spaceType: "",
  spaceLocation: "",
};

const SectionCard = ({ children }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={{
      mb: 2,
      borderRadius: 2,
      p: { xs: 1.5, md: 2 },
      bgcolor: "#fff",
    }}
  >
    {children}
  </Paper>
);

/**
 * Space-centric Smart Room Dashboard.
 * Home ID → spaces dropdown → selected space drives Indoor / Outdoor / Comparison.
 * Sensors are used only to fetch indoor parameters (not shown as device details).
 */
const SmartRoomDashboard = () => {
  const [homeId, setHomeId] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [spaceOptions, setSpaceOptions] = useState([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [spacesError, setSpacesError] = useState("");
  const [homeCache, setHomeCache] = useState(null);

  const [loading, setLoading] = useState(false);
  const [refreshingIndoor, setRefreshingIndoor] = useState(false);
  const [refreshingOutdoor, setRefreshingOutdoor] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [dashboardReady, setDashboardReady] = useState(false);

  const [spaceMeta, setSpaceMeta] = useState(emptySpaceMeta);
  const [coords, setCoords] = useState({ lat: null, lon: null });
  const [indoor, setIndoor] = useState(null);
  const [outdoor, setOutdoor] = useState(null);
  const [indoorUpdatedLabel, setIndoorUpdatedLabel] = useState("");
  const [outdoorUpdatedLabel, setOutdoorUpdatedLabel] = useState("");

  const indoorPollRef = useRef(null);
  const outdoorPollRef = useRef(null);
  const spacesRequestRef = useRef(0);
  const weatherCacheRef = useRef({
    lat: null,
    lon: null,
    raw: null,
    fetchedAt: 0,
    updatedLabel: "",
  });
  const contextRef = useRef({
    deviceId: "",
    lat: null,
    lon: null,
    pollIndoor: false,
    pollWeather: false,
  });

  const clearPolling = useCallback(() => {
    if (indoorPollRef.current) {
      clearInterval(indoorPollRef.current);
      indoorPollRef.current = null;
    }
    if (outdoorPollRef.current) {
      clearInterval(outdoorPollRef.current);
      outdoorPollRef.current = null;
    }
  }, []);

  const applyIndoor = useCallback((statusRaw) => {
    const indoorMapped = mapIndoorStatus(statusRaw || {});
    setIndoor(indoorMapped);
    // Last Updated = this successful fetch (1-min poll), not device updatedTime
    setIndoorUpdatedLabel(resolveIndoorUpdatedLabel());
    return indoorMapped;
  }, []);

  const applyOutdoor = useCallback((weatherRaw, options = {}) => {
    if (!weatherRaw) {
      setOutdoor(null);
      setOutdoorUpdatedLabel("");
      return null;
    }
    const outdoorMapped = mapWeatherStatus(weatherRaw);
    setOutdoor(outdoorMapped);
    const label =
      options.updatedLabel ||
      formatLocalDateTime(outdoorMapped.timeStamp);
    setOutdoorUpdatedLabel(label);
    return { mapped: outdoorMapped, updatedLabel: label };
  }, []);

  const loadWeather = useCallback(
    async (lat, lon, { force = false } = {}) => {
      if (lat == null || lon == null) {
        applyOutdoor(null);
        return {
          ok: false,
          message: ERROR_MESSAGES.MISSING_COORDINATES,
        };
      }

      const cache = weatherCacheRef.current;
      const sameLocation =
        Number(cache.lat) === Number(lat) &&
        Number(cache.lon) === Number(lon) &&
        cache.raw;
      const cacheAgeMs = Date.now() - (cache.fetchedAt || 0);
      const cacheFresh =
        sameLocation && cacheAgeMs < OUTDOOR_POLL_INTERVAL_MS;

      // Same home coords + within 30 min → reuse weather (space switches must not refetch)
      if (!force && cacheFresh) {
        applyOutdoor(cache.raw, { updatedLabel: cache.updatedLabel });
        return { ok: true, message: "", fromCache: true };
      }

      try {
        const weatherRaw = await fetchWeatherDetails(lat, lon);
        const applied = applyOutdoor(weatherRaw);
        weatherCacheRef.current = {
          lat,
          lon,
          raw: weatherRaw,
          fetchedAt: Date.now(),
          updatedLabel: applied?.updatedLabel || "",
        };
        return { ok: true, message: "", fromCache: false };
      } catch (err) {
        applyOutdoor(null);
        return {
          ok: false,
          message: getSmartRoomApiErrorMessage(
            err,
            ERROR_MESSAGES.WEATHER_FAILED
          ),
        };
      }
    },
    [applyOutdoor]
  );

  const loadIndoorStatus = useCallback(
    async ({ sensorDevice }) => {
      const resolvedDeviceId =
        sensorDevice?.deviceId || sensorDevice?.deviceID || sensorDevice?.id;

      if (!resolvedDeviceId) {
        setIndoor(null);
        contextRef.current.deviceId = "";
        return {
          ok: false,
          message: ERROR_MESSAGES.SENSOR_NOT_FOUND,
          deviceId: "",
        };
      }

      try {
        const statusRaw = await fetchSensorHubDeviceStatus(resolvedDeviceId);
        applyIndoor(statusRaw);
        contextRef.current.deviceId = resolvedDeviceId;
        return {
          ok: true,
          message: "",
          deviceId: resolvedDeviceId,
        };
      } catch (err) {
        setIndoor(null);
        contextRef.current.deviceId = "";
        return {
          ok: false,
          message: getSmartRoomApiErrorMessage(
            err,
            ERROR_MESSAGES.DEVICE_STATUS_FAILED
          ),
          deviceId: "",
        };
      }
    },
    [applyIndoor]
  );

  const refreshIndoor = useCallback(async () => {
    const { deviceId: activeDeviceId, pollIndoor } = contextRef.current;
    if (!pollIndoor || !activeDeviceId) return;

    setRefreshingIndoor(true);
    try {
      const statusRaw = await fetchSensorHubDeviceStatus(activeDeviceId);
      applyIndoor(statusRaw);
    } catch {
      setWarnings((prev) =>
        prev.includes(ERROR_MESSAGES.DEVICE_STATUS_FAILED)
          ? prev
          : [...prev, ERROR_MESSAGES.DEVICE_STATUS_FAILED]
      );
    } finally {
      setRefreshingIndoor(false);
    }
  }, [applyIndoor]);

  const refreshOutdoor = useCallback(async () => {
    const { lat, lon, pollWeather } = contextRef.current;
    if (!pollWeather || lat == null || lon == null) return;

    setRefreshingOutdoor(true);
    try {
      const result = await loadWeather(lat, lon, { force: true });
      if (!result.ok && result.message) {
        setWarnings((prev) =>
          prev.includes(result.message) ? prev : [...prev, result.message]
        );
      }
    } finally {
      setRefreshingOutdoor(false);
    }
  }, [loadWeather]);

  const startPolling = useCallback(() => {
    clearPolling();
    const { pollIndoor, pollWeather } = contextRef.current;

    if (pollIndoor) {
      indoorPollRef.current = setInterval(() => {
        refreshIndoor();
      }, INDOOR_POLL_INTERVAL_MS);
    }

    if (pollWeather) {
      outdoorPollRef.current = setInterval(() => {
        refreshOutdoor();
      }, OUTDOOR_POLL_INTERVAL_MS);
    }
  }, [clearPolling, refreshIndoor, refreshOutdoor]);

  useEffect(() => () => clearPolling(), [clearPolling]);

  const resetSpaces = useCallback(() => {
    setSpaceOptions([]);
    setSpaceId("");
    setSpacesError("");
    setHomeCache(null);
  }, []);

  const loadSpacesForHome = useCallback(async (overrideHomeId) => {
    const trimmedHomeId = String(overrideHomeId ?? "").trim();
    if (!trimmedHomeId) {
      resetSpaces();
      return;
    }

    const requestId = ++spacesRequestRef.current;
    setSpacesLoading(true);
    setSpacesError("");
    setError("");

    try {
      const home = normalizeHomeDetails(
        await fetchSmartRoomHomeDetails(trimmedHomeId)
      );

      if (requestId !== spacesRequestRef.current) return;

      if (!home) {
        resetSpaces();
        setSpacesError(ERROR_MESSAGES.HOME_NOT_FOUND);
        return;
      }

      const spaces = listHomeSpaces(home);
      setHomeCache(home);
      setSpaceOptions(spaces);
      setSpaceId("");

      if (!spaces.length) {
        // Home exists but has no spaces — show the dedicated empty-spaces message
        setSpacesError(ERROR_MESSAGES.NO_SPACES);
        return;
      }

      setSpacesError("");
    } catch (err) {
      if (requestId !== spacesRequestRef.current) return;
      resetSpaces();
      setSpacesError(
        getSmartRoomApiErrorMessage(err, ERROR_MESSAGES.SPACES_LOAD_FAILED)
      );
    } finally {
      if (requestId === spacesRequestRef.current) {
        setSpacesLoading(false);
      }
    }
  }, [resetSpaces]);

  // Auto-load spaces shortly after Home ID is entered (no blur/Enter required)
  useEffect(() => {
    const trimmed = homeId.trim();
    if (!trimmed) {
      resetSpaces();
      return undefined;
    }

    const timer = setTimeout(() => {
      loadSpacesForHome(trimmed);
    }, 500);

    return () => clearTimeout(timer);
  }, [homeId, loadSpacesForHome, resetSpaces]);

  const handleHomeIdChange = (event) => {
    setError("");
    setHomeId(event.target.value);
    // Clear previous spaces immediately while typing; debounce effect reloads
    setSpaceOptions([]);
    setSpaceId("");
    setSpacesError("");
    setHomeCache(null);
    weatherCacheRef.current = {
      lat: null,
      lon: null,
      raw: null,
      fetchedAt: 0,
      updatedLabel: "",
    };
    setOutdoor(null);
    setOutdoorUpdatedLabel("");
  };

  const handleHomeIdBlur = () => {
    const trimmed = homeId.trim();
    if (!trimmed) {
      resetSpaces();
      return;
    }
    loadSpacesForHome(trimmed);
  };

  const handleSpaceIdChange = (event) => {
    setError("");
    setSpaceId(event.target.value);
  };

  const loadDashboard = async () => {
    const trimmedHomeId = homeId.trim();
    const trimmedSpaceId = spaceId.trim();

    setLoading(true);
    setError("");
    setWarnings([]);
    setDashboardReady(false);
    setIndoor(null);
    // Keep previous outdoor until weather resolves — same-home space switches
    // reuse the 30-minute weather cache and must not reset Last Updated.
    setSpaceMeta(emptySpaceMeta);
    contextRef.current = {
      deviceId: "",
      lat: null,
      lon: null,
      pollIndoor: false,
      pollWeather: false,
    };
    clearPolling();

    try {
      let home = homeCache;
      if (
        !home ||
        String(home?.homeId ?? "").trim() !== trimmedHomeId
      ) {
        try {
          home = normalizeHomeDetails(
            await fetchSmartRoomHomeDetails(trimmedHomeId)
          );
        } catch (err) {
          throw new Error(
            getSmartRoomApiErrorMessage(err, ERROR_MESSAGES.HOME_NOT_FOUND)
          );
        }
        setHomeCache(home);
        const spaces = listHomeSpaces(home);
        setSpaceOptions(spaces);
      }

      if (!home || (typeof home === "object" && !home.spaces && !home.homeId)) {
        throw new Error(ERROR_MESSAGES.HOME_NOT_FOUND);
      }

      const { lat, lon } = extractHomeCoordinates(home);
      setCoords({ lat, lon });
      contextRef.current.lat = lat;
      contextRef.current.lon = lon;

      const space = findSpaceById(home, trimmedSpaceId);
      if (!space) {
        throw new Error(ERROR_MESSAGES.SPACE_NOT_FOUND);
      }

      const listed =
        spaceOptions.find((s) => s.spaceId === trimmedSpaceId) ||
        listHomeSpaces(home).find((s) => s.spaceId === trimmedSpaceId);

      const spaceName =
        listed?.spaceName || space.spaceName || space.name || "Selected Space";
      const spaceType = listed?.spaceType || "—";

      setSpaceMeta({
        spaceId: trimmedSpaceId,
        spaceName,
        spaceType,
        spaceLocation: listed?.spaceLocation || "",
      });

      const sensorDevice = findOdmsensorHubInSpace(space);

      const [indoorResult, weatherResult] = await Promise.all([
        loadIndoorStatus({ sensorDevice }),
        loadWeather(lat, lon),
      ]);

      if (!indoorResult.ok && !weatherResult.ok) {
        if (indoorResult.message === ERROR_MESSAGES.SENSOR_NOT_FOUND) {
          throw new Error(ERROR_MESSAGES.SENSOR_NOT_FOUND);
        }
        throw new Error(
          indoorResult.message ||
            weatherResult.message ||
            ERROR_MESSAGES.BOTH_FAILED
        );
      }

      const issueMessages = [];
      // Indoor missing message is shown only in the Indoor section (avoid duplicate banners)
      if (!weatherResult.ok && weatherResult.message) {
        issueMessages.push(weatherResult.message);
      }

      contextRef.current.pollIndoor = indoorResult.ok;
      contextRef.current.pollWeather = weatherResult.ok;
      setWarnings(issueMessages);
      setDashboardReady(true);
      startPolling();
    } catch (err) {
      setDashboardReady(false);
      setIndoor(null);
      setOutdoor(null);
      setSpaceMeta(emptySpaceMeta);
      setWarnings([]);
      setError(err?.message || ERROR_MESSAGES.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  // After outdoor arrives, fill space location from weather city when space has none
  useEffect(() => {
    if (!dashboardReady || !outdoor) return;
    setSpaceMeta((prev) => {
      if (prev.spaceLocation) return prev;
      const fromWeather =
        outdoor.city && outdoor.country
          ? `${outdoor.city}, ${outdoor.country}`
          : outdoor.city || "";
      if (!fromWeather) return prev;
      return { ...prev, spaceLocation: fromWeather };
    });
  }, [dashboardReady, outdoor]);

  const handleBackToSearch = () => {
    clearPolling();
    setDashboardReady(false);
    setIndoor(null);
    setOutdoor(null);
    setSpaceMeta(emptySpaceMeta);
    setError("");
    setWarnings([]);
    contextRef.current.pollIndoor = false;
    contextRef.current.pollWeather = false;
  };

  const weatherLocation =
    outdoor?.city && outdoor?.country
      ? `${outdoor.city}, ${outdoor.country}`
      : outdoor?.city || "";

  const showDashboard = !loading && dashboardReady && (indoor || outdoor);
  const refreshing = refreshingIndoor || refreshingOutdoor;

  return (
    <Box sx={{ width: "100%", minWidth: 0, pb: 3 }}>
      {!dashboardReady ? (
        <SearchForm
          homeId={homeId}
          spaceId={spaceId}
          spaceOptions={spaceOptions}
          spacesLoading={spacesLoading}
          spacesError={spacesError}
          onHomeIdChange={handleHomeIdChange}
          onHomeIdBlur={handleHomeIdBlur}
          onSpaceIdChange={handleSpaceIdChange}
          onSubmit={loadDashboard}
          loading={loading}
          error={error}
        />
      ) : null}

      <Box sx={{ mt: dashboardReady ? 0 : 3 }}>
        {loading ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading space details, indoor parameters, and outdoor weather…
            </Alert>
            <DashboardSkeleton />
          </Box>
        ) : null}

        {!loading && !dashboardReady && !error ? (
          <EmptyState
            message="Select a Home ID, choose a Space, then load the space dashboard."
            icon={<HomeWorkOutlinedIcon sx={{ fontSize: 48 }} />}
            height="280px"
            sx={{
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
            }}
          />
        ) : null}

        {!loading && dashboardReady && !indoor && !outdoor ? (
          <Box sx={{ maxWidth: 720, mx: "auto" }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || ERROR_MESSAGES.BOTH_FAILED}
            </Alert>
            <SearchForm
              homeId={homeId}
              spaceId={spaceId}
              spaceOptions={spaceOptions}
              spacesLoading={spacesLoading}
              spacesError={spacesError}
              onHomeIdChange={handleHomeIdChange}
              onHomeIdBlur={handleHomeIdBlur}
              onSpaceIdChange={handleSpaceIdChange}
              onSubmit={() => {
                setDashboardReady(false);
                loadDashboard();
              }}
              loading={loading}
              error=""
            />
          </Box>
        ) : null}

        {showDashboard ? (
          <Box
            sx={{
              bgcolor: "#F4F6F8",
              borderRadius: 2,
              p: { xs: 1, md: 1.5 },
            }}
          >
            {warnings.map((msg) => (
              <Alert key={msg} severity="warning" sx={{ mb: 1.5 }}>
                {msg}
              </Alert>
            ))}

            {refreshing ? (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {refreshingIndoor && refreshingOutdoor
                  ? "Refreshing indoor and outdoor data…"
                  : refreshingIndoor
                    ? "Refreshing indoor space data…"
                    : "Refreshing outdoor weather…"}
              </Alert>
            ) : null}

            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: { xs: 1.5, md: 2 },
              }}
            >
              <DashboardHeader
                spaceName={spaceMeta.spaceName}
                spaceId={spaceMeta.spaceId}
                spaceType={spaceMeta.spaceType}
                onBack={handleBackToSearch}
              />

              <Divider sx={{ mb: 2 }} />

              {indoor ? (
                <SectionCard>
                  <IndoorDashboard
                    indoor={indoor}
                    lat={coords.lat}
                    lon={coords.lon}
                    roomName={spaceMeta.spaceName}
                    lastUpdatedLabel={indoorUpdatedLabel}
                  />
                </SectionCard>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {ERROR_MESSAGES.SENSOR_NOT_FOUND}
                </Alert>
              )}

              {outdoor ? (
                <SectionCard>
                  <OutdoorDashboard
                    outdoor={outdoor}
                    lastUpdatedLabel={outdoorUpdatedLabel}
                  />
                </SectionCard>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {coords.lat == null || coords.lon == null
                    ? ERROR_MESSAGES.MISSING_COORDINATES
                    : ERROR_MESSAGES.WEATHER_FAILED}
                </Alert>
              )}

              <SectionCard>
                <ComparisonSection indoor={indoor} outdoor={outdoor} />
              </SectionCard>

              <SectionCard>
                <LocationSection
                  lat={coords.lat}
                  lon={coords.lon}
                  city={outdoor?.city}
                  weatherLocation={
                    spaceMeta.spaceLocation || weatherLocation
                  }
                />
              </SectionCard>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default SmartRoomDashboard;
